/**
 * BUILD-M5 — supplier connector detail (directory).
 *
 * Full capabilities of one connector from the GLOBAL directory, fetched by id via `getConnector`
 * (GET /api/price-connectors/:id). Reached from the connector directory (connect.tsx) so a user can
 * review what a source supports before connecting it. Read-only.
 *
 * NEVER imports @smartbox/* (mobile invariant 1). Design-system primitives only; no raw style values.
 */
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { getConnector } from '../../../../src/api/pricing';
import { Screen, Text, Card, Button, Badge, ListRow } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';
import { extractErrorMessage, humanize } from '../../../../src/lib/format';

export default function ConnectorDetailScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();

  const connectorQuery = useQuery({
    queryKey: ['price-connector', id],
    queryFn: () => getConnector(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
  const connector = connectorQuery.data;

  if (connectorQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.center}>
          <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading connector" />
        </View>
      </Screen>
    );
  }

  if (connectorQuery.isError || !connector) {
    return (
      <Screen>
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(connectorQuery.error, 'Failed to load this connector.')}
          </Text>
          <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => connectorQuery.refetch()} />
        </Card>
      </Screen>
    );
  }

  const trades = connector.trades_supported ?? [];

  return (
    <Screen scroll>
      <Text variant="title">{connector.display_name}</Text>
      <View style={styles.metaRow}>
        <Badge status="draft" label={humanize(connector.kind)} />
        <Text variant="small" color="secondary">{connector.code}</Text>
      </View>

      <Card>
        <ListRow title="Mechanism" right={<Text variant="small" color="secondary">{humanize(connector.mechanism)}</Text>} />
        <ListRow title="Link method" right={<Text variant="small" color="secondary">{humanize(connector.link_method)}</Text>} />
        <ListRow title="Max capability tier" right={<Text variant="small" color="secondary">{`Tier ${connector.max_capability_tier}`}</Text>} />
        <ListRow title="Status" right={<Text variant="small" color="secondary">{humanize(connector.status)}</Text>} />
      </Card>

      <View style={styles.section}>
        <Text variant="label" color="secondary">Trades supported</Text>
        {trades.length === 0 ? (
          <Card><Text variant="body" color="secondary">No trades listed.</Text></Card>
        ) : (
          <Card>
            <Text variant="body">{trades.map((t) => humanize(t)).join(', ')}</Text>
          </Card>
        )}
      </View>

      <Button
        title="Connect this source"
        onPress={() => router.push('/(app)/supplier-pricing/connect')}
        accessibilityLabel="Connect this source"
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
  section: { gap: theme.spacing.sm },
});
